import React from 'react';
import { Target, Heart, CheckCircle } from 'lucide-react';

// Import gotowych komponentów
import Stats from './Stats';
import Trust from './Trust';
import Newsletter from './Newsletter';
import ContactForm from './ContactForm';

// Dodanie importu czcionki Inter
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

/*
  SEO: Poniższe teksty możesz użyć w tagach <title> i <meta name="description"> 
  za pomocą biblioteki react-helmet lub podobnej, aby poprawić pozycjonowanie.

  Tytuł: O Nas | Szynkowski & Company – Eksperci Prawa i Finansowania dla Twojego Biznesu
  Opis: Szynkowski & Company to zespół doświadczonych prawników i doradców, którzy od lat wspierają firmy w skutecznym pozyskiwaniu dofinansowań i rozwiązywaniu złożonych problemów prawnych. Poznaj naszą misję i wartości, które kierują nami w codziennej pracy.
*/

const AboutUs = () => {
  const whyUsPoints = [
    { text: "Kompleksowość: Oferujemy zarówno doradztwo prawne, jak i wsparcie w pozyskiwaniu finansowania." },
    { text: "Skuteczność: Koncentrujemy się na realnych wynikach i rozwiązaniach, które przynoszą wymierne korzyści." },
    { text: "Indywidualne podejście: Każdy klient i każdy projekt traktowany jest z najwyższą uwagą." },
    { text: "Zaufanie i Partnerstwo: Budujemy długoterminowe relacje oparte na wzajemnym zaufaniu i współpracy." },
  ];

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-32 pb-16 lg:pb-28 overflow-hidden bg-black">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-700/20 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <h1 
            className="text-3xl sm:text-4xl lg:text-6xl text-white mb-4 uppercase leading-tight"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
          >
            O Nas
          </h1>
          <p className="text-lg sm:text-xl text-gray-300">
            Zespół doświadczonych prawników i doradców, którzy wspierają firmy w pozyskiwaniu dofinansowań i rozwiązywaniu złożonych problemów prawnych.
          </p>
        </div>
      </section>

      {/* Misja i Doświadczenie */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-600/20 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6 text-violet-500"/>
                </div>
                <h2 
                  className="text-3xl text-white uppercase"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
                >
                  Nasza misja i wartości
                </h2>
              </div>
              <p className="text-gray-400 text-lg">
                W Szynkowski & Company wierzymy, że sukces naszych klientów jest naszym sukcesem. Naszą misją jest zapewnienie kompleksowego wsparcia, które realnie przyczyni się do rozwoju i bezpieczeństwa biznesu naszych klientów. Kierujemy się zasadami rzetelności, profesjonalizmu i skuteczności.
              </p>
            </div>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-600/20 rounded-lg flex items-center justify-center">
                  <Heart className="w-6 h-6 text-violet-500"/>
                </div>
                <h2 
                  className="text-3xl text-white uppercase"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
                >
                  Doświadczenie i wiedza
                </h2>
              </div>
              <p className="text-gray-400 text-lg">
                Od ponad 10 lat zdobywamy zaufanie firm z różnych branż. Nasze wieloletnie doświadczenie w obszarze funduszy unijnych oraz głęboka znajomość prawa gospodarczego i podatkowego pozwalają nam świadczyć usługi na najwyższym poziomie.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Stats />
      
      {/* Profil Adwokata */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12 items-center">
            <div className="lg:col-span-1">
              <div className="w-full aspect-square rounded-3xl bg-gradient-to-br from-gray-800 to-gray-900 border border-white/10 flex items-center justify-center">
                <img src="konradszynkowski.jpg" alt="Konrad Szynkowski" className="w-full h-full object-cover rounded-3xl"/> 
              </div>
            </div>
            <div className="lg:col-span-2">
              <h2 
                className="text-4xl text-white mb-4 uppercase"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
              >
                Adwokat Konrad Szynkowski
              </h2>
              <p className="text-lg text-violet-500 mb-6">Wpisany na listę adwokatów Okręgowej Rady Adwokackiej w Warszawie.</p>
              <p className="text-gray-400 mb-6">
                Specjalizuje się w pozyskiwaniu dofinansowań oraz obsłudze projektów realizowanych w ramach funduszy Unii Europejskiej przez podmioty publiczne oraz prywatne, w tym:
              </p>
              <ul className="space-y-3 text-gray-400">
                <li className="flex gap-3"><CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0"/><span>Doradztwo prawne przy zawieraniu umów o dofinansowanie.</span></li>
                <li className="flex gap-3"><CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0"/><span>Nadzór prawny nad realizacją i rozliczeniem projektów.</span></li>
                <li className="flex gap-3"><CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0"/><span>Obsługa Bazy Konkurencyjności i procedur przetargowych.</span></li>
                <li className="flex gap-3"><CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0"/><span>Reprezentowanie Beneficjentów podczas kontroli projektów.</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Dlaczego My */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 
            className="text-4xl text-white mb-6 uppercase"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
          >
            Nasz Zespół – eksperci na miarę Twoich potrzeb
          </h2>
          <p className="text-lg text-gray-400 max-w-4xl mx-auto mb-16">
            Nasz zespół tworzą wykwalifikowani prawnicy, doradcy finansowi i specjaliści z różnych dziedzin. Stale podnosimy swoje kwalifikacje, aby być na bieżąco z najnowszymi trendami i zmianami w prawie.
          </p>
          <h3 
            className="text-3xl text-white mb-10 uppercase"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
          >
            Czym wyróżnia się Szynkowski & Company?
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUsPoints.map(item => (
              <div key={item.text} className="backdrop-blur-md bg-white/5 p-6 rounded-2xl text-left border border-white/10 hover:border-violet-600/50 transition-all duration-300">
                <CheckCircle className="w-6 h-6 text-violet-600 mb-3"/>
                <p className="text-gray-300">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Trust />
      <Newsletter />
      <div id="kontakt"><ContactForm /></div>

      <style>{`
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default AboutUs;